    // frontend/src/AppHeader.tsx - NEW Universal Header Component

    import React, { useState, useEffect } from 'react';
    import axios from 'axios';
    import Cookies from 'js-cookie';
    import logoImage from './assets/logo.png';
    import { useAuth } from './AuthContext';
    import { API_URL } from './apiConfig';

    interface AppHeaderProps {
    onBack: () => void;
    onLogout: () => void;
    onProfile: () => void;
    currentPage: string;
    }

    const AppHeader: React.FC<AppHeaderProps> = ({ onBack, onLogout, onProfile, currentPage }) => {
    const { user } = useAuth();
    const [tenantBranding, setTenantBranding] = useState<{
        logo_url: string | null;
        tenant_name: string | null;
        logo_upload_date: string | null;
    } | null>(null);

    // Fetch tenant branding data - only when user is authenticated
    useEffect(() => {
        const fetchTenantBranding = async () => {
            try {
                const token = Cookies.get('access_token') || localStorage.getItem('access_token') || localStorage.getItem('token');
                // Only fetch if we have both user context and token
                if (!user || !token) {
                    return;
                }

                const headers = { Authorization: `Bearer ${token}` };
                const response = await axios.get(`${API_URL}/api/tenants/branding`, { headers });
                setTenantBranding(response.data);
            } catch (error) {
                console.error('Failed to load tenant branding:', error);
            }
        };

        fetchTenantBranding();
    }, [user]); // Re-run when user changes

    // Determine if the current page is the user's primary dashboard page.
    // This prevents the "Back" button from showing on the main screen for any role.
    const isDashboardPage = 
        (user?.role === 'SUPER_ADMIN' && currentPage === 'super-admin') ||
        (user?.role !== 'SUPER_ADMIN' && currentPage === 'dashboard');
    
    // Don't show Profile button on the profile page itself
    const isProfilePage = currentPage === 'profile';


    const styles = {
        header: {
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        padding: 'var(--spacing-md) var(--spacing-lg)',
        borderBottom: '1px solid var(--color-surface-variant)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky' as 'sticky',
        top: 0,
        zIndex: 1001, // Ensure it's above other content
        boxShadow: 'var(--shadow-md)'
        },
        logoContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '2rem', // Increased gap for side-by-side logos
        flex: 1
        },
        evaluToolLogo: {
        height: 'var(--logo-size-md)', // Smaller EvaluTool logo
        width: 'auto'
        },
        tenantLogo: {
        height: 'var(--logo-size-lg)', // Larger tenant logo
        width: 'auto',
        maxWidth: '200px' // Prevent tenant logos from being too wide
        },
        logoSeparator: {
        width: '1px',
        height: '2rem',
        backgroundColor: 'var(--color-surface-variant)',
        margin: '0 0.5rem'
        },
        userInfo: {
        textAlign: 'right' as 'right'
        },
    };

    // Get tenant logo URL from branding data
    const tenantLogoUrl = tenantBranding?.logo_url ? `${API_URL}${tenantBranding.logo_url}` : null;
    
    // Debug logo loading
    React.useEffect(() => {
        if (tenantBranding?.logo_url) {
            console.log('Tenant logo URL:', tenantLogoUrl);
        }
    }, [tenantBranding, tenantLogoUrl]);
    const tenantName = tenantBranding?.tenant_name || 'Organisation';


    return (
        <header style={styles.header}>
        <div style={styles.logoContainer}>
            <img src={logoImage} alt="EvaluTool Logo" style={styles.evaluToolLogo} />
            
            {tenantLogoUrl && (
                <>
                    <div style={styles.logoSeparator}></div>
                    <img 
                        src={tenantLogoUrl} 
                        alt={`${tenantName} Logo`} 
                        style={styles.tenantLogo}
                        onError={(e) => {
                            console.error('Failed to load tenant logo:', tenantLogoUrl);
                            e.currentTarget.style.display = 'none';
                        }}
                        onLoad={() => {
                            console.log('Tenant logo loaded successfully:', tenantLogoUrl);
                        }}
                    />
                </>
            )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={styles.userInfo}>
            <p style={{ margin: 0, fontWeight: '600' }}>Welcome, {user?.full_name}</p>
            <p style={{ margin: 0, fontSize: '0.8rem', color: '#6b7280' }}>
                {/* Format role name nicely, e.g., SUPER_ADMIN -> SUPER ADMIN */}
                {user?.role?.replace(/_/g, ' ')}
            </p>
            </div>
            
            {/* Show Back to Dashboard button on any page that isn't the user's main dashboard */}
            {!isDashboardPage && (
            <button onClick={onBack} className="btn btn-secondary">
                ← Back to Dashboard
            </button>
            )}

            {!isProfilePage && (
            <button 
              onClick={(e) => {
                e.preventDefault();
                onProfile();
              }} 
              className="btn btn-secondary"
              style={{ cursor: 'pointer' }}
            >
            ⚙️ Profile
            </button>
            )}

            <button onClick={onLogout} className="btn btn-destructive">
            Logout
            </button>
        </div>
        </header>
    );
    };

    export default AppHeader;
    